import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { PromoEvent } from '@kawai/shared';
import { fetchPromoEventsByEventType } from '../repositories/promoEvents.js';
import {
  RICHMENU_TAP_FETCH_LIMIT,
  summarizeRichmenuTaps,
  type RichmenuTapSummary,
} from '../richmenuTapStats.js';

// richmenu_tap の raw events 取得は単一の cache entry に集約する。`useRichmenuTapStats`
// （集計版）と `useRichmenuTapEvents`（raw / フィルター用）が同じ queryKey + queryFn を共有し、
// select の有無だけが異なるため、CustomersListPage で両方を呼んでも fetch は 1 回で済む。
const RICHMENU_TAP_QUERY_KEY = (tenantId: string | null | undefined) =>
  ['promo', 'richmenuTapEvents', tenantId] as const;

const fetchRichmenuTapEvents = (tenantId: string | null | undefined) =>
  fetchPromoEventsByEventType(
    tenantId as string,
    'richmenu_tap',
    RICHMENU_TAP_FETCH_LIMIT,
  );

/**
 * LE-00 (promo): リッチメニュータップ（richmenu_tap）を item 別に集計する hook。
 *
 * cache key: `['promo', 'richmenuTapEvents', tenantId]`（raw events を共有 / select で集計）
 * - 行動ログ（promo_events）の richmenu_tap を専用 index で取得し、`summarizeRichmenuTaps` で
 *   item 別の延べ回数 / ユニーク人数に集計する（興味タグ分布とは別軸の分析）。
 * - tenantId が null/undefined の間は disabled。
 */
export function useRichmenuTapStats(
  tenantId: string | null | undefined,
): UseQueryResult<RichmenuTapSummary, Error> {
  return useQuery({
    queryKey: RICHMENU_TAP_QUERY_KEY(tenantId),
    queryFn: () => fetchRichmenuTapEvents(tenantId),
    select: (events) =>
      summarizeRichmenuTaps(events, RICHMENU_TAP_FETCH_LIMIT),
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });
}

/**
 * LE-00 (promo): richmenu_tap の raw events を取得する hook（顧客一覧の item 別フィルター用）。
 *
 * `useRichmenuTapStats` と同一 cache entry を共有する（select 無し = raw のまま）。呼出側で
 * `buildUserItemTapMap` を通して userId → item 集合に変換し、フィルター判定に使う。
 */
export function useRichmenuTapEvents(
  tenantId: string | null | undefined,
): UseQueryResult<PromoEvent[], Error> {
  return useQuery({
    queryKey: RICHMENU_TAP_QUERY_KEY(tenantId),
    queryFn: () => fetchRichmenuTapEvents(tenantId),
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });
}
